// src/catalog/hooks/useCatalogHome.js

import { useEffect, useMemo, useState } from "react";

const apiUrl =
  process.env.REACT_APP_API_URL || "http://localhost:5001/api";

function getAuthToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("accessToken")
  );
}

function normalizeEntries(data) {
  if (!data) return [];

  if (Array.isArray(data.entries)) {
    return data.entries.filter(
      isOrdinaryCatalogDiscoveryEntry
    );
  }

  const groups = [
    data.featured,
    data.certifications,
    data.trainings,
    data.experiments,
    data.collaborations,
    data.new,
    data.popular
  ];

  const byId = new Map();

  groups
    .flat()
    .filter(Boolean)
    .forEach((entry) => {
      const id = entry._id || entry.id || entry.slug;

      if (!id) return;

      byId.set(id, entry);
    });

  return Array.from(byId.values())
    .filter(isOrdinaryCatalogDiscoveryEntry);
}

function normalizeSections(data) {
  if (!data) return {};

  if (data.sections) {
    return data.sections;
  }

  return filterDiscoverySections({
    featured: data.featured || [],
    training: data.trainings || [],
    research: data.experiments || [],
    services: [],
    certifications: data.certifications || [],
    programs: [],
    resources: [],
    new: data.new || [],
    popular: data.popular || [],
    adminInstitutional: [],
    adminComingSoon: [],
    adminInPreparation: [],
    adminClosedArchived: []
  });
}

function isOrdinaryCatalogDiscoveryEntry(entry) {
  return entry?.visibility?.directLinkOnly !== true;
}

function filterDiscoverySections(sections) {
  return Object.fromEntries(
    Object.entries(sections || {}).map(([key, entries]) => [
      key,
      Array.isArray(entries)
        ? entries.filter(isOrdinaryCatalogDiscoveryEntry)
        : entries,
    ])
  );
}

export default function useCatalogHome() {
  const [catalogHome, setCatalogHome] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const endpoint = `${apiUrl}/catalog/app/home`;

  useEffect(() => {
    let cancelled = false;

    async function loadCatalogHome() {
      try {
        setLoading(true);
        setError("");

        const token = getAuthToken();

        if (!token) {
          throw new Error(
            "Catalog request failed: missing auth token"
          );
        }

        const fetchOptions = token
          ? {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          : undefined;

        const response = await fetch(
          endpoint,
          fetchOptions
        );

        if (!response.ok) {
          throw new Error(
            `Catalog request failed: ${response.status}`
          );
        }

        const data = await response.json();

        if (!cancelled) {
          setCatalogHome(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err.message ||
              "Could not load catalog"
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadCatalogHome();

    return () => {
      cancelled = true;
    };
  }, [endpoint]);

  const allEntries = useMemo(() => {
    return normalizeEntries(catalogHome);
  }, [catalogHome]);

  const sections = useMemo(() => {
    return filterDiscoverySections(normalizeSections(catalogHome));
  }, [catalogHome]);

  return {
    catalogHome,
    sections,
    allEntries,
    loading,
    error
  };
}
